import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trophy, TrendDown, Plus } from "@phosphor-icons/react";

export default function VendorComparison() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/vendor-comparison").then((r) => setData(r.data)).catch(() => setData([]));
  }, []);

  if (!data) return <div className="p-8 label-caps animate-pulse">Loading…</div>;

  return (
    <div>
      <PageHeader label="Estimating" title="Best Price">
        <Button asChild className="rounded-none" data-testid="comparison-add-quote">
          <Link to="/quotations"><Plus size={16} className="mr-1" /> New Quotation</Link>
        </Button>
      </PageHeader>

      <div className="p-4 sm:p-8">
        <p className="text-sm text-muted-foreground mb-8 max-w-2xl">
          The cheapest vendor per category, derived from your past quotations. Assign a
          <span className="font-medium text-foreground"> category</span> and
          <span className="font-medium text-foreground"> vendor</span> to your quotes to power this comparison.
        </p>

        {data.length === 0 ? (
          <div className="border border-dashed border-border p-16 text-center">
            <Trophy size={32} weight="bold" className="mx-auto mb-4 text-muted-foreground" />
            <p className="font-heading text-xl font-bold mb-2">No comparison data yet</p>
            <p className="text-muted-foreground text-sm mb-6">
              Create quotations with a category and vendor to see the cheapest options here.
            </p>
            <Button asChild className="rounded-none"><Link to="/quotations">Create a quotation</Link></Button>
          </div>
        ) : (
          <div className="space-y-8">
            {data.map((cat) => (
              <div key={cat.category} className="border border-border" data-testid={`comparison-cat-${cat.category}`}>
                <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary">
                  <div className="flex items-center gap-3">
                    <span className="font-heading font-bold">{cat.category}</span>
                    <Badge className="rounded-none bg-emerald-600 hover:bg-emerald-600">
                      <Trophy size={12} weight="fill" className="mr-1" /> {cat.cheapest_vendor}
                    </Badge>
                  </div>
                  {cat.potential_savings > 0 && (
                    <div className="flex items-center gap-1 text-sm text-emerald-700 font-medium" data-testid={`savings-${cat.category}`}>
                      <TrendDown size={16} weight="bold" />
                      Save up to {money(cat.potential_savings)}
                    </div>
                  )}
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Rank</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead className="text-right">Best Quote</TableHead>
                      <TableHead className="text-right">Avg Quote</TableHead>
                      <TableHead className="text-right">Quotes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cat.vendors.map((v, idx) => (
                      <TableRow key={v.vendor_id} className={v.is_cheapest ? "bg-emerald-50/60 hover:bg-emerald-50" : "hover:bg-secondary"}>
                        <TableCell className="font-mono-fig tabular-nums text-muted-foreground">
                          {idx === 0 ? <Trophy size={16} weight="fill" className="text-emerald-600" /> : `#${idx + 1}`}
                        </TableCell>
                        <TableCell className={`font-medium ${v.is_cheapest ? "text-emerald-800" : ""}`}>{v.vendor_name}</TableCell>
                        <TableCell className={`text-right font-mono-fig tabular-nums font-semibold ${v.is_cheapest ? "text-emerald-700" : ""}`}>
                          {money(v.min_quote)}
                        </TableCell>
                        <TableCell className="text-right font-mono-fig tabular-nums text-muted-foreground">{money(v.avg_quote)}</TableCell>
                        <TableCell className="text-right font-mono-fig tabular-nums">{v.quote_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
